import { SyncCustomFieldAnswerDto } from "@smeetz/custom-field-common";
import Dexie from "dexie";
import dayjs from "dayjs";
import Timeout from "await-timeout";
import { dexieDB } from "@/services/dexie-database";
import { IFileRef, IPendingAnswerSync } from "@/types/db/answer-sync";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { classifySyncError, SyncErrorKind } from "@/db/sync-error";
import { LogLevel, LogService } from "@/services/logging";
import { getCustomFieldService } from "@/services/custom-field.service";
import { buildFilesMapFromRefs } from "@/utils/vue-helpers/custom-fields.helper";
import { initiateAnswerSync, MAX_SYNC_ATTEMPTS } from "@/services/sync-service";

/**
 * Write-ahead enqueue of a completed order's custom-field answer sync.
 *
 * Stores the answer DTOs + file refs as Pending BEFORE any network attempt so the
 * upload is durable even offline, then kicks off the background drain. The file
 * blobs themselves already live in `dexieDB.files` and are rebuilt at drain time.
 */
export async function saveAnswerSyncForOrder(
  orderId: string,
  answers: SyncCustomFieldAnswerDto[],
  fileRefs: IFileRef[]
): Promise<void> {
  if (!orderId || !answers.length) {
    return;
  }

  const creationDateTime = dayjs().toISOString();
  const pending: IPendingAnswerSync = {
    _id: `${orderId}:${creationDateTime}`,
    orderId,
    answers,
    fileRefs,
    syncStatus: SyncStatus.Pending,
    attempts: 0,
    creationDateTime,
  };

  try {
    await dexieDB.transaction(
      "rw",
      dexieDB.customFieldAnswerSyncs,
      async () => {
        const existing = await dexieDB.customFieldAnswerSyncs.get(pending._id);
        pending.version = (existing?.version ?? 0) + 1;
        await dexieDB.customFieldAnswerSyncs.put(pending);
      }
    );
  } catch (err: any) {
    LogService.info(
      `Error storing pending answer sync into local db, id:${pending._id}`,
      { params: { record: pending, err } },
      LogLevel.Error
    );
    throw err;
  }

  // The row payload never mutates after enqueue, so this log holds the exact
  // answers of every later upload attempt — recoverable by this id.
  LogService.info(
    `(compressed) Queueing custom-field answers for sync, id: ${pending._id}, order: ${orderId}, version: ${pending.version}`,
    pending
  );

  initiateAnswerSync();
}

/**
 * Record the outcome of an upload attempt on the row it was made for. Guarded
 * by `version` (see IPendingAnswerSync.version): the outcome is applied as a
 * field-level update, and only when the row is still the snapshot that was
 * uploaded.
 */
async function applyAnswerSyncOutcome(
  posted: IPendingAnswerSync,
  changes: { syncStatus: SyncStatus; attempts?: number }
): Promise<void> {
  await dexieDB.transaction("rw", dexieDB.customFieldAnswerSyncs, async () => {
    const current = await dexieDB.customFieldAnswerSyncs.get(posted._id);
    if (!current) {
      return;
    }
    if (current.version !== posted.version) {
      LogService.info(
        `Sync: answer sync ${posted._id} was re-enqueued (version ${current.version}) while version ${posted.version} was syncing, keeping the newer snapshot Pending`
      );
      await dexieDB.customFieldAnswerSyncs.update(posted._id, {
        syncStatus: SyncStatus.Pending,
      });
      return;
    }
    await dexieDB.customFieldAnswerSyncs.update(posted._id, changes);
  });
}

/**
 * Upload a single queued answer-sync to the backend via the custom-field service.
 * Rebuilds the file map from `dexieDB.files`, then deletes the queue row and the
 * uploaded blobs on success.
 */
async function syncPendingAnswerSync(
  pending: IPendingAnswerSync
): Promise<void> {
  try {
    const files = await buildFilesMapFromRefs(pending.fileRefs);

    LogService.info(
      `Sync: Uploading custom-field answers for order ${pending.orderId}, id: ${pending._id}, attempts so far: ${pending.attempts}`
    );
    await getCustomFieldService().syncAnswers(
      pending.answers,
      Object.keys(files).length ? files : undefined
    );

    // Success — remove the queue row and the uploaded blobs. Version-guarded:
    // a row re-enqueued mid-upload must survive to sync its own snapshot.
    await dexieDB.transaction(
      "rw",
      dexieDB.customFieldAnswerSyncs,
      async () => {
        const current = await dexieDB.customFieldAnswerSyncs.get(pending._id);
        if (!current) {
          return;
        }
        if (current.version !== pending.version) {
          LogService.info(
            `Sync: answer sync ${pending._id} was re-enqueued (version ${current.version}) while version ${pending.version} was syncing, keeping the newer snapshot Pending`
          );
          await dexieDB.customFieldAnswerSyncs.update(pending._id, {
            syncStatus: SyncStatus.Pending,
          });
          return;
        }
        await dexieDB.customFieldAnswerSyncs.delete(pending._id);
      }
    );
    if (pending.fileRefs.length) {
      const fileIds = pending.fileRefs.map((r) => r.fileId);
      try {
        await dexieDB.files.where("_id").anyOf(fileIds).delete();
      } catch (err) {
        LogService.info(
          `Sync: answers uploaded but failed to delete blobs for ${pending._id}`,
          { err }
        );
      }
    }
    LogService.info(
      `Sync: Custom-field answers for ${pending._id} synced and removed from queue`
    );
  } catch (error: any) {
    switch (classifySyncError(error)) {
      case SyncErrorKind.Network:
        // Transient — retry forever without consuming the attempt budget.
        LogService.info(
          `Network Error uploading answers ${pending._id}, will retry`,
          { params: { error } },
          LogLevel.Warning
        );
        await applyAnswerSyncOutcome(pending, {
          syncStatus: SyncStatus.Failed,
        });
        break;

      case SyncErrorKind.Validation:
        // The backend rejects this payload — retrying can never succeed.
        LogService.info(
          `Validation error uploading answers ${pending._id}, marking Ignored (will not retry)`,
          { params: { error } },
          LogLevel.Error
        );
        await applyAnswerSyncOutcome(pending, {
          syncStatus: SyncStatus.Ignored,
          attempts: (pending.attempts || 0) + 1,
        });
        break;

      case SyncErrorKind.Backend: {
        // Other backend failure — retry within the MAX_SYNC_ATTEMPTS budget.
        // Retries the drain still owns are journaled as WARNING; only
        // exhausting the budget ships as ERROR, so one stuck upload raises
        // one alert instead of one per retry.
        const attempts = (pending.attempts || 0) + 1;
        const exhausted = attempts >= MAX_SYNC_ATTEMPTS;
        LogService.info(
          exhausted
            ? `Error uploading answers ${pending._id}: retry budget exhausted after ${attempts} attempts`
            : `Sync: uploading answers ${pending._id} failed (attempt ${attempts}/${MAX_SYNC_ATTEMPTS}), will retry`,
          { error },
          exhausted ? LogLevel.Error : LogLevel.Warning
        );
        await applyAnswerSyncOutcome(pending, {
          syncStatus: SyncStatus.Failed,
          attempts,
        });
        break;
      }
    }
  }
}

/**
 * Drain queued answer-syncs from Dexie to the backend.
 *
 * Answers upload independently of the order's persist state — `syncAnswers` has no
 * FK dependency on the order being persisted first (the original inline flow synced
 * answers before the order). Pending is prioritised over Failed, mirroring the
 * orders queue.
 */
export async function syncDexieAnswerSyncs(limit = 10): Promise<void> {
  if (!dexieDB.isOpen()) {
    LogService.info("Database not open, skipping answer sync");
    return;
  }

  let candidates: IPendingAnswerSync[] = [];
  try {
    const pendings = await dexieDB.customFieldAnswerSyncs
      .where("[syncStatus+creationDateTime]")
      .between(
        [SyncStatus.Pending, Dexie.minKey],
        [SyncStatus.Pending, Dexie.maxKey]
      )
      .reverse()
      .toArray();
    const faileds = await dexieDB.customFieldAnswerSyncs
      .where("[syncStatus+attempts]")
      .between([SyncStatus.Failed, 0], [SyncStatus.Failed, MAX_SYNC_ATTEMPTS])
      .toArray();
    candidates = [...pendings, ...faileds].slice(0, limit);
  } catch (error) {
    LogService.info(
      `Sync: Error fetching pending answer syncs from Dexie db`,
      { error },
      LogLevel.Error
    );
    return;
  }

  for (let toSync of candidates) {
    if (!dexieDB.isOpen()) break;

    // Field-level update + re-read: never overwrite the row payload with a
    // stale in-memory copy, and upload the freshest snapshot.
    try {
      await dexieDB.customFieldAnswerSyncs.update(toSync._id, {
        syncStatus: SyncStatus.InProgress,
      });
      toSync = (await dexieDB.customFieldAnswerSyncs.get(toSync._id)) ?? toSync;
    } catch (error) {
      LogService.info(
        `Sync: Error marking answer sync ${toSync._id} as InProgress`,
        { error },
        LogLevel.Error
      );
    }

    await syncPendingAnswerSync(toSync);
    await Timeout.set(1000);
  }
}

/**
 * Count answer-syncs still awaiting upload (Pending + Failed within retry budget).
 */
export async function unsyncedAnswerSyncsCount(): Promise<number> {
  const [pendingCount, failedCount] = await Promise.all([
    dexieDB.customFieldAnswerSyncs
      .where("syncStatus")
      .equals(SyncStatus.Pending)
      .count(),
    dexieDB.customFieldAnswerSyncs
      .where("[syncStatus+attempts]")
      .between([SyncStatus.Failed, 0], [SyncStatus.Failed, MAX_SYNC_ATTEMPTS])
      .count(),
  ]);

  return pendingCount + failedCount;
}
